import { useCallback, useEffect, useState } from "react";
import type { ExpandedState } from "@tanstack/react-table";

import { useWorkStatuses } from "@/hooks/useWorkStatuses";
import { dataProcessorWorker } from "@/lib/data-processor-worker";
import type { WorkTreeModel } from "@/models/WorkTreeModel";

interface UseTableTreeDataProps {
  data: WorkTreeModel[];
}

export const useTableTreeData = ({ data }: UseTableTreeDataProps) => {
  const { workStatuses, completeStatusId } = useWorkStatuses();
  const [tableData, setTableData] = useState<WorkTreeModel[]>(data);
  const [expanded, setExpanded] = useState<ExpandedState>(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // Sync tableData when data prop changes (e.g., after query invalidation/refetch).
  // Bail out on identical refs, and on empty→empty, so a caller that allocates
  // `|| []` every render cannot trigger an infinite setState loop.
  useEffect(() => {
    setTableData((prev) => {
      if (prev === data) return prev;
      if (prev.length === 0 && data.length === 0) return prev;
      return data;
    });
  }, [data]);

  // Update worker with complete status ID when it changes
  useEffect(() => {
    if (completeStatusId !== null) {
      dataProcessorWorker.setCompleteStatusId(completeStatusId);
    }
  }, [completeStatusId, workStatuses]);

  // Helper function to get all row IDs for expanding
  const getAllRowIds = useCallback(
    (items: WorkTreeModel[], prefix = ""): Record<string, boolean> => {
      return items.reduce<Record<string, boolean>>((expanded, item, index) => {
        const rowId = prefix ? `${prefix}.${index}` : `${index}`;
        if (item.subRows && item.subRows.length > 0) {
          // Recursively expand children and merge results immutably
          const childExpanded = getAllRowIds(item.subRows, rowId);
          return {
            ...expanded,
            [rowId]: true,
            ...childExpanded,
          };
        }
        return expanded;
      }, {});
    },
    [],
  );

  // Helpers for overdue/upcoming (by calculated start date)
  const isCompleted = useCallback(
    (item: WorkTreeModel): boolean =>
      completeStatusId != null && item.statusId === completeStatusId,
    [completeStatusId],
  );
  const isStartDateInPast = useCallback(
    (startDate: string | null | undefined): boolean => {
      if (!startDate) return false;
      const start = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      start.setHours(0, 0, 0, 0);
      return start < today && !Number.isNaN(start.getTime());
    },
    [],
  );
  const isStartDateInFutureOrNull = useCallback(
    (startDate: string | null | undefined): boolean => {
      if (!startDate) return true;
      const start = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      start.setHours(0, 0, 0, 0);
      return !Number.isNaN(start.getTime()) && start >= today;
    },
    [],
  );

  // Optimized helper function to match status filter
  const matchesStatusFilter = useCallback(
    (item: WorkTreeModel, statusFilter: string): boolean => {
      if (statusFilter === "incomplete") {
        return completeStatusId === null
          ? item.statusId === null
          : item.statusId === null || item.statusId !== completeStatusId;
      }
      if (statusFilter === "overdue") {
        return isStartDateInPast(item.startDate) && !isCompleted(item);
      }
      if (statusFilter === "upcoming") {
        return isStartDateInFutureOrNull(item.startDate) && !isCompleted(item);
      }
      return item.statusId === statusFilter;
    },
    [completeStatusId, isCompleted, isStartDateInPast, isStartDateInFutureOrNull],
  );

  // Async filtering with worker support and loading states
  const filterTreeData = useCallback(
    async (
      data: WorkTreeModel[],
      searchTerm: string,
      statusFilter: string,
    ): Promise<WorkTreeModel[]> => {
      const hasSearchFilter = searchTerm.trim().length > 0;
      const hasStatusFilter = statusFilter !== "all";

      // Early return if no filters applied
      if (!hasSearchFilter && !hasStatusFilter) {
        return data;
      }

      try {
        // Use worker for filtering without loading callback (managed in main component)
        const result = await dataProcessorWorker.filterTreeData(
          data,
          searchTerm,
          statusFilter,
        );
        return result;
      } catch (error) {
        console.error("Error filtering data with worker:", error);
        // Fallback to synchronous filtering
        return filterTreeDataSync(data, searchTerm, statusFilter);
      }
    },
    [completeStatusId],
  );

  // Synchronous fallback filtering function
  const filterTreeDataSync = useCallback(
    (
      data: WorkTreeModel[],
      searchTerm: string,
      statusFilter: string,
    ): WorkTreeModel[] => {
      const hasSearchFilter = searchTerm.trim().length > 0;
      const hasStatusFilter = statusFilter !== "all";

      // Early return if no filters applied
      if (!hasSearchFilter && !hasStatusFilter) {
        return data;
      }

      const searchLower = hasSearchFilter ? searchTerm.toLowerCase() : "";

      const filterRecursive = (items: WorkTreeModel[]): WorkTreeModel[] => {
        const result: WorkTreeModel[] = [];

        for (const item of items) {
          // Check name match for search
          const nameMatches =
            !hasSearchFilter ||
            (item.name?.toLowerCase().includes(searchLower) ?? false);

          // Check status match
          let statusMatches = true;
          if (hasStatusFilter) {
            statusMatches = matchesStatusFilter(item, statusFilter);
          }

          // Filter children recursively
          let filteredSubRows: WorkTreeModel[] | undefined;
          if (item.subRows && item.subRows.length > 0) {
            filteredSubRows = filterRecursive(item.subRows);
          }

          const hasMatchingChildren =
            filteredSubRows && filteredSubRows.length > 0;

          // Include item if it matches both filters or has matching children
          if ((nameMatches && statusMatches) || hasMatchingChildren) {
            result.push({
              ...item,
              subRows: filteredSubRows,
            });
          }
        }

        return result;
      };

      return filterRecursive(data);
    },
    [matchesStatusFilter],
  );

  // Service worker-based hierarchical sorting function
  const sortTreeData = useCallback(
    async (dataToSort: WorkTreeModel[]): Promise<WorkTreeModel[]> => {
      try {
        const sorted = await dataProcessorWorker.sortTreeData(
          dataToSort,
          (isLoading) => {
            setIsProcessing(isLoading);
          },
        );
        return sorted;
      } catch (error) {
        console.error("Error sorting data:", error);
        // Return original data as fallback
        return dataToSort;
      }
    },
    [],
  );

  // Clear cache when data changes
  const clearFilterCache = useCallback(async () => {
    try {
      await dataProcessorWorker.clearCache();
    } catch (error) {
      console.error("Error clearing cache:", error);
    }
  }, []);

  return {
    tableData,
    setTableData,
    expanded,
    setExpanded,
    getAllRowIds,
    matchesStatusFilter,
    filterTreeData,
    sortTreeData,
    clearFilterCache,
    isProcessing,
  };
};
